import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { formatReadableAmount } from '../../../Common/FormatFunctions/FormatAmount';
import { ScaleLoader } from 'react-spinners';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Typography, Box, FormControl, Select, MenuItem,
} from '@mui/material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PageNotFound from '../../../Common/PageNotFound/PageNotFound';
import useProgramPermissions from '../../../Common/Permissions/useProgramPermissions';
import Swal from 'sweetalert2';
import BackButton from '../../../Common/Buttons/BackButton';
import { useNavigate } from 'react-router-dom';

const apikey = process.env.REACT_APP_API;
const FONT = "'Signika','Segoe UI',system-ui,sans-serif";

/* ───────────────────────── columns ───────────────────────── */

const SCREEN_COLUMNS = [
    { label: 'Doc No', key: 'Doc_No', width: '6%', screenHidden: true },   // shown under Doc Date on screen, still used in print
    { label: 'Doc Date', key: 'Doc_Date', width: '8%' },
    { label: 'Supplier Name', key: 'SupplierName', width: '14%' },
    { label: 'Division', key: 'System_Name_E', width: '9%' },
    { label: 'Narration', key: 'Narration', width: '15%' },
    { label: 'Payable', key: 'Payable', width: '13%', numeric: true, isTotal: true },
    { label: 'Paid Amount', key: 'PAID', width: '8%', numeric: true, isTotal: true, screenHidden: true },
    { label: 'Balance', key: 'balance', width: '8%', numeric: true, isTotal: true, screenHidden: true },
    { label: 'Value Date', key: 'Value_date', width: '10%', isPaymentInput: true, sortable: true },
    { label: 'Amount Due', key: 'amount', width: '11%', isPaymentInput: true },
    { label: 'Remark', key: 'remark', width: '12%', isPaymentInput: true },
    { label: 'Bill', key: 'billimage_path', width: '5%', isBill: true },
    { label: 'Pay Now', key: 'status', width: '6%', isStatus: true },
];

// Columns actually drawn on screen (PRINT_COLUMNS below stays untouched)
const VISIBLE_SCREEN_COLUMNS = SCREEN_COLUMNS.filter((c) => !c.screenHidden);

// Stable empty object: a fresh `{}` on every render would defeat React.memo on PaymentRow
const EMPTY_INPUT = {};

const PRINT_COLUMNS = SCREEN_COLUMNS.filter((c) => !c.isStatus && !c.isPaymentInput && !c.isBill);

/* ───────────────────────── helpers ───────────────────────── */

// Local (not UTC) today as YYYY-MM-DD, so it is correct in IST
const getTodayStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toDDMMYYYY = (v) => {
    const s = String(v ?? '').slice(0, 10);
    const [y, m, d] = s.split('-');
    return y && m && d ? `${d}/${m}/${y}` : s;
};

const initials = (n) =>
    String(n || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const DATE_TABS = [
    { value: 'TODAY', label: "Today's" },
    { value: 'ALL', label: 'All' },
    { value: 'UPTO', label: 'Up to date' },
];

/* ───────────────────────── icons ───────────────────────── */

const Svg = ({ children, size = 18, color = 'currentColor', sw = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw}
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IconSearch = (p) => <Svg {...p}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></Svg>;
const IconClose = (p) => <Svg {...p}><path d="M6 6l12 12M18 6L6 18" /></Svg>;
const IconFilter = (p) => <Svg {...p}><path d="M4 5h16l-6 8v6l-4-2v-4L4 5z" /></Svg>;
const IconInbox = (p) => <Svg {...p}><path d="M4 13l2-8h12l2 8M4 13v6h16v-6M4 13h5l1 2h4l1-2h5" /></Svg>;
const IconSend = (p) => <Svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></Svg>;
const IconImage = (p) => <Svg {...p}><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></Svg>;

/* ───────────────────────── styles ───────────────────────── */

const PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Signika:wght@300;400;500;600;700&display=swap');
:root {
  --vp-ink:#1c1f3d; --vp-mute:#6f7496; --vp-line:#e8eaf5; --vp-bg:#f3f4fb; --vp-card:#fff;
  --vp-brand:#5146e5; --vp-brand-2:#8467ff; --vp-brand-soft:#eff0ff;
  --vp-ok:#0f8a60; --vp-ok-soft:#e1f6ec; --vp-warn:#b36b00; --vp-warn-soft:#fff2d9;
  --vp-font:'Signika','Segoe UI',system-ui,sans-serif;
}
.vp-page, .vp-page * { font-family:var(--vp-font); }
.vp-page { background:var(--vp-bg); color:var(--vp-ink); border-radius:14px; min-height:100vh; }
.vp-page .MuiTableCell-root, .vp-page .MuiInputBase-root, .vp-page .MuiTablePagination-root, .vp-page .MuiTablePagination-root * { font-family:var(--vp-font) !important; }

.vp-head { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:14px; flex-wrap:wrap; }
.vp-head-left { display:flex; align-items:center; gap:14px; }
.vp-title { font-size:24px; font-weight:600; letter-spacing:-.01em; margin:0; line-height:1.15; }
.vp-sub { font-size:13.5px; color:var(--vp-mute); margin:3px 0 0; }

.vp-btn { display:inline-flex; align-items:center; justify-content:center; gap:7px; padding:9px 16px; border-radius:12px; font-size:14px; font-weight:500; line-height:1.2; text-transform:none; letter-spacing:normal; white-space:nowrap; cursor:pointer; border:1px solid var(--vp-line) !important; background:#fff !important; color:var(--vp-ink) !important; transition:background .2s, border-color .2s, transform .15s, box-shadow .2s; }
.vp-btn:hover { background:var(--vp-brand-soft) !important; border-color:#cdd1ff !important; }
.vp-btn:active { transform:scale(.97); }
.vp-btn:focus-visible { outline:2px solid var(--vp-brand); outline-offset:2px; }
.vp-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
.vp-btn-primary { background:linear-gradient(135deg,var(--vp-brand),var(--vp-brand-2)) !important; color:#fff !important; border-color:transparent !important; box-shadow:0 8px 20px rgba(81,70,229,.3); }
.vp-btn-primary:hover { background:linear-gradient(135deg,#4338d8,#7655f5) !important; box-shadow:0 10px 24px rgba(81,70,229,.38); }
.vp-btn-ghost { border-color:transparent !important; background:transparent !important; color:var(--vp-brand) !important; padding:5px 10px; font-size:13px; }
.vp-btn-ghost:hover { background:var(--vp-brand-soft) !important; }

.vp-card { background:var(--vp-card); border:1px solid var(--vp-line); border-radius:18px; overflow:hidden; box-shadow:0 2px 8px rgba(28,31,61,.04); }
.vp-toolbar { display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; padding:14px 18px 0; }
.vp-tabs { display:flex; gap:4px; border-bottom:1px solid var(--vp-line); flex:1 1 auto; overflow-x:auto; }
.vp-tab { position:relative; display:inline-flex; align-items:center; gap:8px; padding:10px 14px 12px; border:0 !important; background:transparent !important; font-size:14.5px; font-weight:500; color:var(--vp-mute) !important; cursor:pointer; white-space:nowrap; transition:color .2s; }
.vp-tab:hover { color:var(--vp-ink) !important; }
.vp-tab::after { content:''; position:absolute; left:10px; right:10px; bottom:-1px; height:3px; border-radius:3px 3px 0 0; background:var(--vp-brand); transform:scaleX(0); transition:transform .3s cubic-bezier(.2,.8,.2,1); }
.vp-tab-on { color:var(--vp-brand) !important; font-weight:600; }
.vp-tab-on::after { transform:scaleX(1); }
.vp-count { min-width:22px; height:20px; padding:0 7px; border-radius:10px; background:#eceefa; color:var(--vp-mute); font-size:12px; font-weight:600; display:inline-flex; align-items:center; justify-content:center; transition:background .2s, color .2s; }
.vp-tab-on .vp-count { background:var(--vp-brand); color:#fff; }
.vp-search { position:relative; width:290px; max-width:100%; margin-bottom:6px; }
.vp-search svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--vp-mute); pointer-events:none; }
.vp-search input { width:100%; height:40px; padding:0 12px 0 38px; border:1px solid var(--vp-line); border-radius:12px; font-size:14px; color:var(--vp-ink); background:#fafbff; outline:none; transition:border-color .2s, box-shadow .2s, background .2s; }
.vp-search input:focus { border-color:var(--vp-brand); background:#fff; box-shadow:0 0 0 4px rgba(81,70,229,.12); }

.vp-frow { display:flex; align-items:center; gap:10px 18px; flex-wrap:wrap; padding:12px 18px; border-bottom:1px solid var(--vp-line); background:#fcfcff; }
.vp-ftitle { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:var(--vp-mute); }
.vp-fsel { display:inline-flex; align-items:center; gap:8px; }
.vp-fsel label { margin:0; font-size:13px; color:var(--vp-mute); }
.vp-datein { height:36px; padding:0 10px; border:1px solid var(--vp-line); border-radius:10px; font-size:14px; color:var(--vp-ink); background:#fff; outline:none; animation:vpPop .3s cubic-bezier(.2,.9,.3,1.2) both; }
.vp-datein:focus { border-color:var(--vp-brand); box-shadow:0 0 0 3px rgba(81,70,229,.12); }
.vp-fchips { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-left:auto; }
.vp-fchip { display:inline-flex; align-items:center; gap:6px; padding:4px 6px 4px 11px; border-radius:999px; background:var(--vp-brand-soft); color:var(--vp-brand); font-size:12.5px; font-weight:500; animation:vpPop .25s cubic-bezier(.2,.9,.3,1.2) both; }
.vp-fchip button { width:18px; height:18px; border-radius:50%; border:0 !important; background:rgba(81,70,229,.16) !important; color:var(--vp-brand) !important; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; padding:0; }
.vp-fchip button:hover { background:var(--vp-brand) !important; color:#fff !important; }
.vp-tmeta { padding:10px 18px; font-size:13px; color:var(--vp-mute); }

.vp-table { min-width:1400px; }
.vp-table th { background:#fff !important; color:var(--vp-mute) !important; font-size:13px !important; font-weight:600 !important; padding:12px 12px !important; border-bottom:1px solid var(--vp-line) !important; white-space:nowrap; user-select:none; }
.vp-table th.vp-sortable { cursor:pointer; }
.vp-table th.vp-sortable:hover, .vp-table th.vp-sorted { color:var(--vp-brand) !important; }
.vp-sort { margin-left:5px; font-size:12px; opacity:.4; }
.vp-sorted .vp-sort { opacity:1; }
.vp-table td { padding:12px !important; font-size:14px !important; border-bottom:1px solid #f0f1f9 !important; vertical-align:middle; color:var(--vp-ink); }
.vp-table tbody tr { transition:background .15s; }
.vp-table tbody tr:hover td { background:#f7f8ff; }
.vp-table tbody tr.vp-row-on td { background:#f3f1ff; }
.vp-table tfoot td { background:#fafbff !important; font-weight:600 !important; border-top:1px solid var(--vp-line) !important; border-bottom:0 !important; font-size:14px !important; }
.vp-num { font-variant-numeric:tabular-nums; font-weight:600 !important; }
.vp-strong { font-weight:600; }
.vp-muted { color:var(--vp-mute); font-size:12.5px; }
.vp-sup { display:flex; align-items:center; gap:10px; }
.vp-av { width:34px; height:34px; border-radius:11px; background:linear-gradient(135deg,#e7e5ff,#d5ccff); color:var(--vp-brand); font-size:12.5px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; flex:none; }
.vp-supname { font-weight:500; line-height:1.25; }
.vp-clamp { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; max-width:240px; }

.vp-stack { display:grid; grid-template-columns:auto 1fr; column-gap:14px; row-gap:3px; width:max-content; font-size:13.5px; }
.vp-stack span { color:var(--vp-mute); }
.vp-stack b { text-align:right; font-weight:500; font-variant-numeric:tabular-nums; }
.vp-stack .vp-bal { color:var(--vp-brand); font-weight:700; }
.vp-stack .vp-bal-l { color:var(--vp-ink); font-weight:600; }

.vp-input { width:100%; min-width:120px; height:38px; padding:0 10px; border:1px solid var(--vp-line); border-radius:10px; font-size:14px; color:var(--vp-ink); background:#fff; outline:none; transition:border-color .2s, box-shadow .2s; }
.vp-input:focus { border-color:var(--vp-brand); box-shadow:0 0 0 3px rgba(81,70,229,.12); }
.vp-amt { position:relative; }
.vp-amt > span { position:absolute; left:11px; top:9px; color:var(--vp-mute); font-size:14px; pointer-events:none; }
.vp-amt .vp-input { padding-left:26px; text-align:right; font-weight:600; font-variant-numeric:tabular-nums; }
.vp-hint { font-size:11.5px; color:var(--vp-mute); margin-top:3px; text-align:right; }
.vp-text { width:100%; min-width:150px; padding:7px 10px; border:1px solid var(--vp-line); border-radius:10px; font-size:13.5px; line-height:1.3; color:var(--vp-ink); background:#fff; outline:none; resize:none; transition:border-color .2s, box-shadow .2s; }
.vp-text:focus { border-color:var(--vp-brand); box-shadow:0 0 0 3px rgba(81,70,229,.12); }

.vp-bill { width:36px; height:36px; border-radius:11px; border:0 !important; background:var(--vp-brand-soft) !important; color:var(--vp-brand) !important; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:background .2s, transform .15s; }
.vp-bill:hover { background:#e0e2ff !important; transform:translateY(-1px); }
.vp-check { width:24px; height:24px; accent-color:var(--vp-brand); cursor:pointer; }
.vp-check:disabled { cursor:not-allowed; opacity:.4; }

.vp-empty { text-align:center; padding:54px 12px; color:var(--vp-mute); }
.vp-empty b { display:block; color:var(--vp-ink); margin:10px 0 2px; font-size:16px; font-weight:600; }

.vp-bar { position:sticky; bottom:0; z-index:20; margin-top:12px; background:#fff; border:1px solid var(--vp-line); border-radius:16px; padding:12px 18px; display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; box-shadow:0 -8px 24px rgba(28,31,61,.07); }
.vp-stats { display:flex; gap:30px; flex-wrap:wrap; }
.vp-stat span { display:block; font-size:12.5px; color:var(--vp-mute); }
.vp-stat b { font-size:18px; font-weight:600; font-variant-numeric:tabular-nums; }
.vp-stat-sel b { color:var(--vp-brand); }

@keyframes vpPop { from { opacity:0; transform:scale(.85); } to { opacity:1; transform:none; } }
@media (prefers-reduced-motion:reduce) { .vp-page * { animation:none !important; transition:none !important; } }
`;

/* ───────────────────────── small components ───────────────────────── */

const FilterSelect = ({ id, label, value, onChange, options }) => (
    <div className="vp-fsel">
        <label htmlFor={id}>{label}</label>
        <FormControl size="small">
            <Select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                MenuProps={{ PaperProps: { sx: { borderRadius: '12px', mt: 0.5, fontFamily: FONT } } }}
                sx={{
                    height: 36, minWidth: 150, borderRadius: '10px', fontSize: 14, fontFamily: FONT,
                    color: '#1c1f3d', background: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e8eaf5' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b9bffb' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5146e5', borderWidth: '1px' },
                }}
            >
                {options.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 14, fontFamily: FONT }}>{o.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    </div>
);

const BillCell = ({ path }) => {
    if (!path) return <span className="vp-muted">Not uploaded</span>;
    return (
        <button
            type="button"
            className="vp-bill"
            title="View bill"
            onClick={(e) => {
                e.stopPropagation();
                window.open(`${apikey}/other-purchase-bill-image/${path}`, '_blank', 'noopener,noreferrer');
            }}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <IconImage size={18} />
        </button>
    );
};

const AmountStack = ({ payable, paid, balance }) => (
    <div className="vp-stack">
        <span>Payable</span><b>{payable}</b>
        <span>Paid</span><b>{paid}</b>
        <span className="vp-bal-l">Balance</span><b className="vp-bal">{balance}</b>
    </div>
);

const PaymentRow = React.memo(function PaymentRow({
    item, rowInput, amount, rowReady, checked, onFieldChange, onToggle, onSaveValueDate, canEdit
}) {
    const savedDate = item.Value_date ?? '';
    const currentDate = rowInput.Value_date ?? savedDate;
    const [amtFocused, setAmtFocused] = useState(false);
    const isOn = checked && rowReady;

    return (
        <TableRow className={isOn ? 'vp-row-on' : ''}>
            <TableCell>
                <div className="vp-strong">{item.Doc_Date}</div>
                <div className="vp-muted">Doc No {item.Doc_No}</div>
            </TableCell>
            <TableCell>
                <div className="vp-sup">
                    <span className="vp-av">{initials(item.SupplierName)}</span>
                    <span className="vp-supname">{item.SupplierName}</span>
                </div>
            </TableCell>
            <TableCell>{item.System_Name_E}</TableCell>
            <TableCell><div className="vp-clamp" title={item.Narration}>{item.Narration}</div></TableCell>
            <TableCell>
                <AmountStack
                    payable={formatReadableAmount(item.Payable)}
                    paid={formatReadableAmount(item.PAID || 0.0)}
                    balance={formatReadableAmount(item.balance || 0.0)}
                />
            </TableCell>
            <TableCell>
                <input
                    type="date"
                    className="vp-input"
                    value={currentDate}
                    onChange={(e) => {
                        const v = e.target.value;
                        onFieldChange(item.opid, 'Value_date', v);
                        // auto-save as soon as a new valid date is picked
                        if (v && v !== savedDate && canEdit) onSaveValueDate(item.opid, v);
                    }}
                />
            </TableCell>
            <TableCell>
                <div className="vp-amt">
                    <span>₹</span>
                    <input
                        type="text"
                        inputMode="decimal"
                        className="vp-input"
                        value={amtFocused || amount === '' ? amount : formatReadableAmount(amount)}
                        onFocus={() => setAmtFocused(true)}
                        onBlur={() => setAmtFocused(false)}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (!/^\d*\.?\d*$/.test(value)) return;   // digits and one decimal point only
                            const numValue = parseFloat(value);
                            const balance = parseFloat(item.balance) || 0;
                            if (value === '' || isNaN(numValue)) {
                                onFieldChange(item.opid, 'amount', value);
                                return;
                            }
                            if (numValue > balance) {
                                alert(`Amount cannot exceed the balance of ${formatReadableAmount(balance)}`);
                                onFieldChange(item.opid, 'amount', String(balance));
                            } else {
                                onFieldChange(item.opid, 'amount', value);
                            }
                        }}
                    />
                </div>
                <div className="vp-hint">Max {formatReadableAmount(item.balance || 0)}</div>
            </TableCell>
            <TableCell>
                <textarea
                    className="vp-text"
                    value={rowInput.remark || ''}
                    rows={2}
                    placeholder="Remark"
                    onChange={(e) => onFieldChange(item.opid, 'remark', e.target.value)}
                />
            </TableCell>
            <TableCell align="center">
                <BillCell path={item.billimage_path} />
            </TableCell>
            <TableCell align="center">
                <input
                    type="checkbox"
                    className="vp-check"
                    checked={isOn}
                    disabled={!rowReady}
                    onChange={() => onToggle(item.opid)}
                    title={!rowReady ? 'Enter an Amount first' : 'Select for payment'}
                />
            </TableCell>
        </TableRow>
    );
});

/* ───────────────────────── page ───────────────────────── */

const OtherPurchasePayment = () => {
    const companyCode = sessionStorage.getItem('Company_Code');
    const Year_Code = sessionStorage.getItem('Year_Code');
    const Company_Name = sessionStorage.getItem('Company_Name');
    const { permissions, canView } = useProgramPermissions('/otherpurchasepayment_Utility');
    const navigate = useNavigate();

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'Doc_No', direction: 'desc' });
    const [valueDateFilter, setValueDateFilter] = useState('ALL');
    const [dateMode, setDateMode] = useState(false);   // false = Today/All, true = "up to date"
    const [upToDate, setUpToDate] = useState('');      // YYYY-MM-DD
    const [divisionFilter, setDivisionFilter] = useState('ALL');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Payment inputs and checked rows are tracked separately from reportData
    // so typing/checking never triggers a re-sort of the whole table.
    const [paymentInputs, setPaymentInputs] = useState({});
    const [checkedOpids, setCheckedOpids] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);

    const API_URL = `${apikey}/getall-OtherPurchasePayment`;
    const SAVE_PAYMENT_URL = `${apikey}/other-purchase-payment`;

    const abortControllerRef = useRef(null);

    const dateView = dateMode ? 'UPTO' : valueDateFilter;

    const handleDateView = (v) => {
        if (v === 'UPTO') {
            setDateMode(true);
        } else {
            setDateMode(false);
            setUpToDate('');
            setValueDateFilter(v);
        }
    };

    const fetchReportData = useCallback(async () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        try {
            const res = await axios.get(API_URL, {
                params: { Company_Code: companyCode, Year_Code: Year_Code },
                signal: controller.signal,
            });
            setReportData(Array.isArray(res.data?.all_data) ? res.data.all_data : []);
            setError('');
        } catch (err) {
            if (!axios.isCancel(err)) setError('Error fetching report');
        } finally {
            setLoading(false);
        }
    }, [API_URL, companyCode, Year_Code]);

    // Single fetch effect (gated by view permission)
    useEffect(() => {
        if (canView && companyCode && Year_Code) fetchReportData();
        return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
    }, [canView, companyCode, Year_Code, fetchReportData]);

    const divisionOptions = useMemo(() => {
        const names = [...new Set(reportData.map((r) => r.System_Name_E).filter(Boolean))].sort();
        return [{ value: 'ALL', label: 'All divisions' }, ...names.map((n) => ({ value: n, label: n }))];
    }, [reportData]);

    // Everything except the date view
    const baseItems = useMemo(() => {
        let items = [...reportData];
        if (divisionFilter !== 'ALL') items = items.filter((i) => i.System_Name_E === divisionFilter);
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            items = items.filter((item) => Object.values(item).some((val) => String(val).toLowerCase().includes(q)));
        }
        return items;
    }, [reportData, divisionFilter, searchTerm]);

    const dateCounts = useMemo(() => {
        const today = getTodayStr();
        return {
            TODAY: baseItems.filter((i) => String(i.Value_date ?? '').slice(0, 10) === today).length,
            ALL: baseItems.length,
        };
    }, [baseItems]);

    const filteredAndSortedData = useMemo(() => {
        let items = [...baseItems];
        const today = getTodayStr();
        if (dateMode && upToDate) {
            items = items.filter((item) => {
                const d = String(item.Value_date ?? '').slice(0, 10);
                return d >= today && d <= upToDate;
            });
        } else if (valueDateFilter === 'TODAY') {
            items = items.filter((item) => String(item.Value_date ?? '').slice(0, 10) === today);
        }
        if (sortConfig.key) {
            const dir = sortConfig.direction === 'asc' ? 1 : -1;
            items.sort((a, b) => {
                const ra = a[sortConfig.key] ?? '';
                const rb = b[sortConfig.key] ?? '';
                if (ra !== '' && rb !== '' && !isNaN(ra) && !isNaN(rb)) return (Number(ra) - Number(rb)) * dir;
                const sa = String(ra).toLowerCase();
                const sb = String(rb).toLowerCase();
                if (sa < sb) return -1 * dir;
                if (sa > sb) return 1 * dir;
                return 0;
            });
        }
        return items;
    }, [baseItems, sortConfig, valueDateFilter, dateMode, upToDate]);

    // Back to page 1 whenever the result set changes shape
    useEffect(() => { setPage(0); }, [searchTerm, valueDateFilter, dateMode, upToDate, divisionFilter, rowsPerPage]);

    const pagedData = useMemo(
        () => filteredAndSortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredAndSortedData, page, rowsPerPage]
    );

    const grandTotals = useMemo(() => {
        return filteredAndSortedData.reduce(
            (acc, item) => {
                acc.Payable += parseFloat(item.Payable) || 0;
                acc.balance += parseFloat(item.balance) || 0;
                acc.partial += parseFloat(item.PAID) || 0;
                return acc;
            },
            { Payable: 0, balance: 0, partial: 0 }
        );
    }, [filteredAndSortedData]);

    const activeChips = [
        searchTerm && { key: 'q', label: `Search: ${searchTerm}`, clear: () => setSearchTerm('') },
        divisionFilter !== 'ALL' && { key: 'd', label: `Division: ${divisionFilter}`, clear: () => setDivisionFilter('ALL') },
        dateMode && upToDate && { key: 'u', label: `Up to ${toDDMMYYYY(upToDate)}`, clear: () => setUpToDate('') },
    ].filter(Boolean);

    const clearFilters = () => {
        setSearchTerm('');
        setDivisionFilter('ALL');
        setUpToDate('');
    };

    // New column -> asc, same column -> toggle
    const handleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    // Builds the PDF directly with jsPDF so it does not depend on a PdfPreview label/template.
    const handleGeneratePDF = () => {
        if (filteredAndSortedData.length === 0) {
            Swal.fire({ icon: 'info', title: 'No data', text: 'There are no records to print.' });
            return;
        }

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text((Company_Name || '').toUpperCase(), pageWidth / 2, 12, { align: 'center' });
        doc.setFontSize(11);
        doc.text('Vendor Payment', pageWidth / 2, 18, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`Printed on: ${new Date().toLocaleString('en-IN')}`, pageWidth - 14, 18, { align: 'right' });

        const body = filteredAndSortedData.map((item) => PRINT_COLUMNS.map((col) => {
            if (col.key === 'Doc_Date') return item.Doc_Date;
            if (col.numeric) return formatReadableAmount(item[col.key]);
            return item[col.key] ?? '';
        }));

        const columnStyles = {};
        PRINT_COLUMNS.forEach((c, i) => { if (c.numeric) columnStyles[i] = { halign: 'right' }; });

        autoTable(doc, {
            startY: 22,
            head: [PRINT_COLUMNS.map((c) => c.label)],
            body,
            foot: [[
                { content: 'GRAND TOTAL', colSpan: 5, styles: { halign: 'right' } },
                { content: formatReadableAmount(grandTotals.Payable), styles: { halign: 'right' } },
                { content: formatReadableAmount(grandTotals.balance), styles: { halign: 'right' } },
                { content: formatReadableAmount(grandTotals.partial), styles: { halign: 'right' } },
            ]],
            showFoot: 'lastPage',
            theme: 'grid',
            margin: { left: 14, right: 14, bottom: 14 },
            styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak', valign: 'middle' },
            headStyles: { fillColor: [81, 70, 229], textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: [238, 240, 255], textColor: 0, fontStyle: 'bold' },
            columnStyles,
        });

        const totalPages = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
        }

        window.open(doc.output('bloburl'), '_blank');
    };

    const EXCEL_COLUMNS = [
        ...PRINT_COLUMNS.slice(0, 2),                              // Doc No, Doc Date
        SCREEN_COLUMNS.find((c) => c.key === 'Value_date'),        // Value Date
        ...PRINT_COLUMNS.slice(2),                                 // Supplier ... Balance
    ];

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const todayStr = new Date().toLocaleDateString('en-IN');
        const wsData = [
            [Company_Name?.toUpperCase()],
            ['Vendor Payment Report'],
            [`Date: ${todayStr}`],
            [],
            EXCEL_COLUMNS.map((c) => c.label),
        ];

        filteredAndSortedData.forEach((item) => {
            wsData.push(EXCEL_COLUMNS.map((col) =>
                col.key === 'Value_date' ? toDDMMYYYY(item.Value_date) : item[col.key]
            ));
        });

        wsData.push([]);
        wsData.push(['', '', '', '', 'GRAND TOTAL', grandTotals.Payable, grandTotals.balance, grandTotals.partial]);

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'OtherPurchasePayment');
        XLSX.writeFile(wb, `OtherPurchasePayment.xlsx`);
    };

    // Amount defaults to the row's balance until the user edits it
    const getRowAmount = useCallback((item, input) => {
        if (input && input.amount !== undefined) return input.amount;
        const bal = parseFloat(item?.balance) || 0;
        return bal > 0 ? String(Math.round(bal * 100) / 100) : '';
    }, []);

    const itemsByOpid = useMemo(() => {
        const map = {};
        reportData.forEach((it) => { map[it.opid] = it; });
        return map;
    }, [reportData]);

    const isRowReady = useCallback((opid) => {
        const item = itemsByOpid[opid];
        if (!item) return false;
        const amt = parseFloat(getRowAmount(item, paymentInputs[opid]));
        return !isNaN(amt) && amt > 0;
    }, [paymentInputs, itemsByOpid, getRowAmount]);

    // Total of Amount for CHECKED rows only
    const selectedTotal = useMemo(() => {
        let total = 0;
        checkedOpids.forEach((opid) => {
            if (!isRowReady(opid)) return;
            total += parseFloat(getRowAmount(itemsByOpid[opid], paymentInputs[opid])) || 0;
        });
        return total;
    }, [checkedOpids, paymentInputs, itemsByOpid, isRowReady, getRowAmount]);

    const selectedCount = useMemo(
        () => [...checkedOpids].filter((opid) => isRowReady(opid)).length,
        [checkedOpids, isRowReady]
    );

    const canSubmit = selectedCount > 0;

    const handleStatusToggle = useCallback((opid) => {
        setCheckedOpids((prev) => {
            const next = new Set(prev);
            if (next.has(opid)) next.delete(opid); else next.add(opid);
            return next;
        });
    }, []);

    const handlePaymentFieldChange = useCallback((opid, field, value) => {
        setPaymentInputs((prev) => ({
            ...prev,
            [opid]: { ...prev[opid], [field]: value },
        }));
    }, []);

    const handleSaveValueDate = useCallback(async (opid, newDate) => {
        const item = itemsByOpid[opid];
        if (!item) return;

        const username = sessionStorage.getItem('username');
        const User_Id = sessionStorage.getItem('User_ID');

        try {
            const { data } = await axios.get(`${apikey}/get-OtherPurchaseSelectedRecord`, {
                params: { Company_Code: companyCode, Year_Code: Year_Code, Doc_No: item.Doc_No },
            });
            const record = data.selected_Record_data;

            if (record.LockedRecord) {
                alert(`This record is locked by ${record.LockedUser}`);
                return;
            }

            await axios.put(
                `${apikey}/update-OtherPurchase`,
                { ...record, Value_date: newDate, Modified_By: username, User_Id }
            );

            setReportData((prev) => prev.map((r) => (r.opid === opid ? { ...r, Value_date: newDate } : r)));
            setPaymentInputs((prev) => {
                const { Value_date, ...rest } = prev[opid] || {};
                return { ...prev, [opid]: rest };
            });
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.response?.data?.error || 'Failed to update payment date',
                confirmButtonText: 'OK',
            });
        }
    }, [itemsByOpid, companyCode, Year_Code]);

    const handleBulkPaymentSubmit = async () => {
        const today = getTodayStr();   // local date, not UTC

        const entriesToSubmit = [...checkedOpids]
            .filter((opid) => isRowReady(opid))
            .map((opid) => [opid, {
                ...paymentInputs[opid],
                amount: getRowAmount(itemsByOpid[opid], paymentInputs[opid]),
            }]);

        if (entriesToSubmit.length === 0) {
            alert('Please fill Amount and check "Pay Now" for at least one record');
            return;
        }

        setSubmitting(true);
        try {
            await Promise.all(
                entriesToSubmit.map(([opid, val]) =>
                    axios.post(SAVE_PAYMENT_URL, {
                        purc_id: opid,
                        payment_date: val.Value_date ?? itemsByOpid[opid]?.Value_date ?? today,
                        amount: val.amount,
                        remark: val.remark || '',
                    })
                )
            );
            window.location.reload();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to save one or more payments');
            setSubmitting(false);
        }
    };

    if (canView === null) return null;
    if (canView === false) return <PageNotFound />;

    const rangeStart = filteredAndSortedData.length === 0 ? 0 : page * rowsPerPage + 1;
    const rangeEnd = Math.min((page + 1) * rowsPerPage, filteredAndSortedData.length);

    return (
        <Box className="vp-page" sx={{ padding: '16px', marginTop: '10px' }}>
            <style>{PAGE_CSS}</style>

            {/* Header */}
            <div className="vp-head">
                <div className="vp-head-left">
                    {/* <BackButton onClick={() => navigate('/dashboard')} /> */}
                    <div>
                        <h1 className="vp-title">Vendor Payment</h1>
                      
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="vp-btn" onClick={handleGeneratePDF}>Print PDF</button>
                    <button className="vp-btn" onClick={handleExportToExcel}>Export Excel</button>
                </div>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            <div className="vp-card">
                {/* Date tabs + search */}
                <div className="vp-toolbar">
                    <div className="vp-tabs" role="tablist">
                        {DATE_TABS.map((t) => (
                            <button
                                key={t.value}
                                type="button"
                                role="tab"
                                aria-selected={dateView === t.value}
                                className={`vp-tab ${dateView === t.value ? 'vp-tab-on' : ''}`}
                                onClick={() => handleDateView(t.value)}
                            >
                                {t.label}
                                {t.value !== 'UPTO' && <span className="vp-count">{dateCounts[t.value]}</span>}
                            </button>
                        ))}
                    </div>
                    <div className="vp-search">
                        <IconSearch size={16} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search supplier, doc no, narration…"
                            aria-label="Search payments"
                        />
                    </div>
                </div>

                {/* Filter row */}
                <div className="vp-frow">
                    <span className="vp-ftitle"><IconFilter size={15} /> Filters</span>
                    <FilterSelect
                        id="divisionFilter" label="Division" value={divisionFilter}
                        onChange={setDivisionFilter} options={divisionOptions}
                    />
                    {dateMode && (
                        <div className="vp-fsel">
                            <label htmlFor="upToDate">Show from today up to</label>
                            <input
                                id="upToDate"
                                type="date"
                                className="vp-datein"
                                value={upToDate}
                                min={getTodayStr()}
                                onChange={(e) => setUpToDate(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="vp-fchips">
                        {activeChips.map((c) => (
                            <span key={c.key} className="vp-fchip">
                                {c.label}
                                <button type="button" onClick={c.clear} aria-label={`Remove ${c.label}`}><IconClose size={11} sw={3} /></button>
                            </span>
                        ))}
                        {activeChips.length > 0 && (
                            <button type="button" className="vp-btn vp-btn-ghost" onClick={clearFilters}>Clear all</button>
                        )}
                    </div>


                           <div className="vp-bar">
                <div className="vp-stats">
                    <div className="vp-stat"><span>Payable</span><b>{formatReadableAmount(grandTotals.Payable)}</b></div>
                    <div className="vp-stat"><span>Paid</span><b>{formatReadableAmount(grandTotals.partial)}</b></div>
                    <div className="vp-stat"><span>Balance</span><b>{formatReadableAmount(grandTotals.balance)}</b></div>
                    <div className="vp-stat vp-stat-sel">
                        <span>Selected ({selectedCount})</span>
                        <b>{formatReadableAmount(selectedTotal)}</b>
                    </div>
                </div>
                <button
                    className="vp-btn vp-btn-primary"
                    onClick={handleBulkPaymentSubmit}
                    disabled={!canSubmit || submitting || permissions.canSave === 'N' || permissions.canView === 'N' || permissions.canEdit === 'N' || permissions.canDelete === 'N'}
                >
                    {submitting ? 'Saving…' : `Send ${selectedCount || ''} for Make payment`.replace('  ', ' ')} <IconSend size={16} />
                </button>
            </div>

                </div>

                <div className="vp-tmeta">
                    Showing {rangeStart}–{rangeEnd} of {filteredAndSortedData.length} records
                </div>

                {/* Table */}
                <TableContainer sx={{ maxHeight: 640 }}>
                    <Table stickyHeader size="small" className="vp-table">
                        <TableHead>
                            <TableRow>
                                {VISIBLE_SCREEN_COLUMNS.map((col, idx) => {
                                    const sortable = col.sortable || (!col.isStatus && !col.isPaymentInput && !col.isBill);
                                    const isSorted = sortConfig.key === col.key;
                                    const icon = isSorted ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕';
                                    return (
                                        <TableCell
                                            key={`${col.key}-${idx}`}
                                            align={col.isStatus || col.isBill ? 'center' : 'left'}
                                            className={`${sortable ? 'vp-sortable' : ''} ${isSorted ? 'vp-sorted' : ''}`}
                                            onClick={sortable ? () => handleSort(col.key) : undefined}
                                            style={{ width: col.width }}
                                        >
                                            {col.label}
                                            {sortable && <span className="vp-sort">{icon}</span>}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedData.map((item) => (
                                <PaymentRow
                                    key={item.opid}
                                    item={item}
                                    rowInput={paymentInputs[item.opid] || EMPTY_INPUT}
                                    amount={getRowAmount(item, paymentInputs[item.opid])}
                                    rowReady={isRowReady(item.opid)}
                                    checked={checkedOpids.has(item.opid)}
                                    onFieldChange={handlePaymentFieldChange}
                                    onToggle={handleStatusToggle}
                                    onSaveValueDate={handleSaveValueDate}
                                    canEdit={permissions?.canEdit !== 'N'}
                                />
                            ))}
                            {!loading && pagedData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={VISIBLE_SCREEN_COLUMNS.length} sx={{ border: 0 }}>
                                        <div className="vp-empty">
                                            <IconInbox size={38} />
                                            <b>No bills found</b>
                                            {dateView === 'TODAY'
                                                ? 'Nothing is due today. Try the All tab or pick an up-to date.'
                                                : 'Try removing a filter or changing the search.'}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredAndSortedData.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ borderTop: '1px solid #e8eaf5', fontFamily: FONT }}
                />
            </div>

            {/* Action bar */}
     
            {/* Loader */}
            {(loading || submitting) && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <ScaleLoader color="#5146e5" size={60} />
                        <Typography sx={{ mt: 2, fontWeight: 600, color: '#5146e5', fontFamily: FONT }}>
                            {submitting ? 'Saving payment…' : 'Loading data…'}
                        </Typography>
                    </Box>
                </div>
            )}
        </Box>
    );
};

export default OtherPurchasePayment;